import { Icon } from '../../components/ui/Icon.js';
import { AuthLayout } from './AuthLayout.js';
import { useState, type FormEvent } from 'react';
import { login, verifyTwoStep, ApiClientError } from '../../lib/api/client.js';

export function LoginPage({ onSignedIn }: { onSignedIn: () => Promise<void> }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Set once the password is accepted for somebody with two-step verification on.
  const [ticket, setTicket] = useState<string | null>(null);
  const [code, setCode] = useState('');

  const submitCode = async (e: FormEvent) => {
    e.preventDefault();
    if (!ticket) return;
    setBusy(true); setError(null);
    try {
      await verifyTwoStep(ticket, code);
      await onSignedIn();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed');
      setCode('');
      setBusy(false);
    }
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const next = await login(email, password);
      if (next) { setTicket(next.mfaTicket); setPassword(''); setBusy(false); return; }
      await onSignedIn();
    } catch (err) {
      // The server deliberately does not distinguish "no such account" from
      // "wrong password" (doc 06 §2.1), and neither does this.
      setError(
        err instanceof ApiClientError && err.status === 503
          ? 'The sign-in service is temporarily unavailable. Please try again shortly.'
          : err instanceof Error ? err.message : 'Sign in failed',
      );
      setBusy(false);
    }
  };

  return (
    <AuthLayout>
        <div style={{ marginBottom: 20 }}>
          <h1 className="arc-h1">{ticket ? 'Two-step verification' : 'Sign in'}</h1>
          <p className="arc-muted" style={{ marginTop: 4, fontSize: 13 }}>
            {ticket ? 'Enter the 6-digit code from your authenticator app.' : 'Use your ArcNet account.'}
          </p>
        </div>

        {ticket ? (
          <form onSubmit={submitCode} noValidate>
            {error && (
              <div className="arc-banner arc-banner-danger" style={{ marginBottom: 16 }} role="alert">
                <Icon name="warning" size={14} />
                <span>{error}</span>
              </div>
            )}
            <div className="arc-field">
              <label className="arc-label" htmlFor="code">Code</label>
              <input id="code" className="arc-input arc-mono" autoComplete="one-time-code" inputMode="text"
                autoFocus required maxLength={20} value={code}
                onChange={(e) => setCode(e.target.value)} aria-invalid={error !== null} />
              <div className="arc-hint">Lost your phone? Enter one of your recovery codes instead.</div>
            </div>
            <button type="submit" className="arc-btn arc-btn-primary" style={{ width: '100%', marginTop: 4 }}
              disabled={busy || code.trim().length < 6}>
              {busy ? 'Checking…' : 'Verify'}
            </button>
            <button type="button" className="arc-btn arc-btn-ghost" style={{ width: '100%', marginTop: 8 }}
              onClick={() => { setTicket(null); setCode(''); setError(null); }}>
              Back
            </button>
          </form>
        ) : (
        <form onSubmit={submit} noValidate>
          {error && (
            <div className="arc-banner arc-banner-danger" style={{ marginBottom: 16 }} role="alert">
              <Icon name="warning" size={14} />
              <span>{error}</span>
            </div>
          )}

          <div className="arc-field">
            <label className="arc-label" htmlFor="email">Email address</label>
            <input
              id="email" type="email" className="arc-input"
              autoComplete="username" required autoFocus
              value={email} onChange={(e) => setEmail(e.target.value)}
              aria-invalid={error !== null}
            />
          </div>

          <div className="arc-field">
            <label className="arc-label" htmlFor="password">Password</label>
            <input
              id="password" type="password" className="arc-input"
              autoComplete="current-password" required
              value={password} onChange={(e) => setPassword(e.target.value)}
              aria-invalid={error !== null}
            />
          </div>

          <button
            type="submit"
            className="arc-btn arc-btn-primary"
            style={{ width: '100%', marginTop: 4 }}
            disabled={busy || !email || !password}
          >
            {busy ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
        )}

        <p className="arc-subtle" style={{ marginTop: 16, fontSize: 12 }}>
          Can't sign in? Ask your company's ArcNet admin.
        </p>
          </AuthLayout>
  );
}
